import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { startOfWeek, addDays, format, isSameDay, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getWeekSchedule,
  getPendingExceptions,
  getUpcomingSchedule,
  clearError,
} from '@/store/slices/scheduleSlice';
import { getMyTimeOff } from '@/store/slices/timeOffSlice';
import { isTentativeDate } from '@/utils/scheduleWeek';
import { groupUpcoming, withRequestedShifts } from '@/utils/upcomingSchedule';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import LoadingScreen from '@/components/common/LoadingScreen';
import RequestTimeModal from './RequestTimeModal';
import RequestTimeOffModal from '@/screens/timeoff/RequestTimeOffModal';

/**
 * How far ahead the Upcoming section asks for — the backend maximum. The loader
 * projects ~4 weeks; days already in "This week" are then removed, so the copy
 * never promises a fixed span.
 */
const UPCOMING_DAYS = 30;

interface DayEvent {
  id: number;
  name: string;
  start: string;
  end: string;
  type?: string;
  displayTime?: string;
}

const prettyDate = (iso: string): string => {
  try {
    return format(parseISO(iso), 'EEE, MMM d');
  } catch {
    return iso;
  }
};

/** Minutes as the backend formats scheduled hours ("3h 15m"). */
const formatMinutes = (total: number): string => `${Math.floor(total / 60)}h ${total % 60}m`;

const ScheduleScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    weekSchedule,
    pendingExceptions,
    isLoading,
    error,
    upcomingSchedule,
    isLoadingUpcoming,
    upcomingError,
  } = useSelector((state: RootState) => state.schedule);

  const navigation = useNavigation<any>();
  const [modalOpen, setModalOpen] = useState(false);
  /** Day the "Request time off" modal is open for (null = closed). */
  const [timeOffDate, setTimeOffDate] = useState<string | null>(null);
  /** Upcoming weeks the user opened/closed; unset = default (only the first is open). */
  const [openWeeks, setOpenWeeks] = useState<Record<string, boolean>>({});

  const loadUpcoming = useCallback(() => {
    dispatch(getUpcomingSchedule(UPCOMING_DAYS));
  }, [dispatch]);

  const load = useCallback(() => {
    dispatch(getWeekSchedule());
    dispatch(getPendingExceptions());
    loadUpcoming();
  }, [dispatch, loadUpcoming]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Recomputed each time the tab is focused, so a screen left open overnight
  // moves its week (and the "Tentative" cut-off) to the new day.
  const [today, setToday] = useState(() => new Date());
  useFocusEffect(
    useCallback(() => {
      setToday((prev) => (isSameDay(prev, new Date()) ? prev : new Date()));
    }, [])
  );
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), [today]);

  // Group this week's events by date.
  // Requested time counts as scheduled: approval is a manager formality staff never see.
  const eventsByDate = useMemo(() => {
    const map: Record<string, DayEvent[]> = {};
    const events = withRequestedShifts(weekSchedule?.events ?? [], pendingExceptions);
    for (const e of events) {
      const key = e.eventDate;
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push({
        id: e.id,
        name: e.name,
        start: e.start,
        end: e.end,
        type: 'type' in e ? e.type : undefined,
        displayTime: e.displayTime,
      });
    }
    Object.values(map).forEach((list) => list.sort((a, b) => a.start.localeCompare(b.start)));
    return map;
  }, [weekSchedule, pendingExceptions]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Upcoming: everything after the last day "This week" shows (Mon–Sun), by Sun–Sat week.
  const shownThrough = format(addDays(weekStart, 6), 'yyyy-MM-dd');
  const upcomingWeeks = useMemo(
    () =>
      groupUpcoming(
        withRequestedShifts(upcomingSchedule?.events ?? [], pendingExceptions),
        shownThrough,
        today
      ),
    [upcomingSchedule, pendingExceptions, shownThrough, today]
  );

  if (isLoading && !weekSchedule) {
    return <LoadingScreen message="Loading your schedule…" />;
  }

  const weekRange = `${format(weekStart, 'MMM d')} – ${format(addDays(weekStart, 6), 'MMM d')}`;
  const weekFrom = format(weekStart, 'yyyy-MM-dd');
  const weekIds = new Set((weekSchedule?.events ?? []).map((e) => e.id));
  const requestedMinutes = pendingExceptions
    .filter((p) => !weekIds.has(p.eventId) && p.eventDate >= weekFrom && p.eventDate <= shownThrough)
    .reduce((sum, p) => sum + (p.durationMinutes ?? 0), 0);
  const scheduledHours =
    requestedMinutes > 0
      ? formatMinutes((weekSchedule?.totalScheduledMinutes ?? 0) + requestedMinutes)
      : weekSchedule?.totalScheduledHours ?? '—';

  return (
    <>
      <ScrollView
        style={styles.screen}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={load}
            tintColor={colors.accent}
            colors={[colors.accent]}
          />
        }>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.kicker}>{weekRange}</Text>
          <Text style={styles.headline}>SCHEDULE</Text>
          <View style={styles.rule} />
          <View style={styles.payRow}>
            <Text style={styles.payValue}>{scheduledHours}</Text>
            <Text style={styles.payLabel}>SCHEDULED THIS WEEK</Text>
          </View>
        </View>

        {/* Request action */}
        <TouchableOpacity
          style={styles.requestBtn}
          activeOpacity={0.85}
          onPress={() => setModalOpen(true)}>
          <Icon name="add-circle" size={22} color={colors.accent} />
          <Text style={styles.requestBtnText}>REQUEST TIME</Text>
        </TouchableOpacity>

        {/* Time off */}
        <TouchableOpacity
          style={styles.timeOffLink}
          activeOpacity={0.7}
          onPress={() => navigation.navigate('TimeOff')}>
          <Icon name="airplane-outline" size={20} color={colors.inkSoft} />
          <Text style={styles.timeOffLinkText}>Time off requests</Text>
          <Icon name="chevron-forward" size={18} color={colors.inkFaint} />
        </TouchableOpacity>


        {/* This week */}
        <Text style={styles.sectionTitle}>This week</Text>
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDate[key] ?? [];
          const isToday = isSameDay(day, today);
          // After the current Sun–Sat week (e.g. this view's trailing Sunday) the
          // schedule is projected and may still change.
          const tentative = dayEvents.length > 0 && isTentativeDate(key, today);
          return (
            <View key={key} style={styles.dayRow}>
              <View style={[styles.dayRail, isToday && styles.dayRailToday]}>
                <Text style={[styles.dayName, isToday && styles.dayTextToday]}>
                  {format(day, 'EEE').toUpperCase()}
                </Text>
                <Text style={[styles.dayNum, isToday && styles.dayTextToday]}>
                  {format(day, 'd')}
                </Text>
              </View>
              <View style={styles.dayBody}>
                {tentative && <Text style={styles.tentativeTag}>Tentative — may change</Text>}
                {dayEvents.length > 0 ? (
                  dayEvents.map((e) => (
                    <View key={e.id} style={styles.eventRow}>
                      <Text style={styles.eventTime}>
                        {e.displayTime ?? `${e.start} – ${e.end}`}
                      </Text>
                      <Text style={styles.eventName}>{e.name}</Text>
                    </View>
                  ))
                ) : (
                  <Text style={styles.dayOff}>Off</Text>
                )}
              </View>
            </View>
          );
        })}

        {/* Upcoming (the next few weeks, beyond this week) */}
        <Text style={[styles.sectionTitle, styles.upcomingTitle]}>Upcoming</Text>
        {isLoadingUpcoming && !upcomingSchedule ? (
          <View style={styles.upcomingState}>
            <ActivityIndicator color={colors.accent} />
            <Text style={styles.upcomingStateText}>Loading upcoming shifts…</Text>
          </View>
        ) : upcomingError && !upcomingSchedule ? (
          <View style={styles.upcomingState}>
            <Text style={styles.upcomingStateText}>Couldn't load upcoming shifts.</Text>
            <TouchableOpacity
              style={[styles.linkTouch, styles.linkTouchCentered]}
              onPress={loadUpcoming}
              activeOpacity={0.7}>
              <Text style={styles.linkText}>Try again</Text>
            </TouchableOpacity>
          </View>
        ) : upcomingWeeks.length === 0 ? (
          <View style={styles.upcomingState}>
            <Text style={styles.upcomingStateText}>Nothing else scheduled in the next few weeks yet</Text>
          </View>
        ) : (
          upcomingWeeks.map((week, index) => {
            const open = openWeeks[week.weekStart] ?? index === 0;
            return (
              <View key={week.weekStart} style={styles.weekBlock}>
                <TouchableOpacity
                  style={styles.weekHeader}
                  activeOpacity={0.7}
                  onPress={() => setOpenWeeks((prev) => ({ ...prev, [week.weekStart]: !open }))}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: open }}>
                  <View style={styles.weekHeaderText}>
                    <Text style={styles.weekTitle}>Week of {format(parseISO(week.weekStart), 'MMM d')}</Text>
                    <Text style={styles.weekMeta}>
                      {week.shiftCount} {week.shiftCount === 1 ? 'shift' : 'shifts'}
                      {week.tentative ? ' · Tentative — may change' : ''}
                    </Text>
                  </View>
                  <Icon
                    name={open ? 'chevron-up' : 'chevron-down'}
                    size={20}
                    color={colors.inkSoft}
                  />
                </TouchableOpacity>
                {open &&
                  week.days.map((d) => {
                    const day = parseISO(d.date);
                    return (
                      <View key={d.date} style={styles.dayRow}>
                        <View style={styles.dayRail}>
                          <Text style={styles.dayName}>{format(day, 'EEE').toUpperCase()}</Text>
                          <Text style={styles.dayNum}>{format(day, 'd')}</Text>
                        </View>
                        <View style={styles.dayBody}>
                          {d.events.map((e) => (
                            <View key={e.id} style={styles.eventRow}>
                              <Text style={styles.eventTime}>
                                {e.displayTime ?? `${e.start} – ${e.end}`}
                              </Text>
                              <Text style={styles.eventName}>{e.name}</Text>
                            </View>
                          ))}
                          <TouchableOpacity
                            style={styles.linkTouch}
                            onPress={() => setTimeOffDate(d.date)}
                            activeOpacity={0.7}
                            accessibilityLabel={`Request time off on ${prettyDate(d.date)}`}>
                            <Text style={styles.linkText}>Request time off</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })}
              </View>
            );
          })
        )}

      </ScrollView>

      <RequestTimeOffModal
        visible={timeOffDate !== null}
        initialDate={timeOffDate ?? undefined}
        onClose={() => setTimeOffDate(null)}
        onSubmitted={() => dispatch(getMyTimeOff())}
      />

      <RequestTimeModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
      />
    </>
  );
};

const styles = StyleSheet.create({
  upcomingTitle: { marginTop: space.xl },
  upcomingState: {
    alignItems: 'center',
    paddingVertical: space.lg,
  },
  upcomingStateText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.xs,
    textAlign: 'center',
  },
  /** Text links stay small visually but get a ≥44pt tap target. */
  linkTouch: {
    minHeight: 44,
    minWidth: 44,
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  linkTouchCentered: { alignSelf: 'center', alignItems: 'center' },
  linkText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
  },
  weekBlock: { marginBottom: space.md },
  weekHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    marginBottom: space.sm,
    minHeight: 44,
  },
  weekHeaderText: { flex: 1 },
  weekTitle: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
  },
  weekMeta: {
    fontFamily: font.bodyMed,
    fontSize: 12,
    color: colors.inkSoft,
    marginTop: 1,
  },
  tentativeTag: {
    fontFamily: font.bodySemi,
    fontSize: 11,
    letterSpacing: 0.4,
    color: colors.inkSoft,
    marginBottom: 2,
  },
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  content: {
    padding: space.lg,
    paddingBottom: space.xxl,
  },

  // Header
  header: {
    marginBottom: space.lg,
  },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 56,
    lineHeight: 58,
    color: colors.ink,
    letterSpacing: -1,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  payRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  payValue: {
    fontFamily: font.display,
    fontSize: 30,
    lineHeight: 32,
    color: colors.ink,
  },
  payLabel: {
    fontFamily: font.bodySemi,
    fontSize: 11,
    letterSpacing: 1.8,
    color: colors.inkFaint,
    marginLeft: space.sm,
  },

  // Request button
  requestBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 54,
    borderRadius: radius.md,
    backgroundColor: colors.surface,
    borderWidth: 2,
    borderColor: colors.accent,
    marginBottom: space.lg,
  },
  requestBtnText: {
    fontFamily: font.display,
    fontSize: 21,
    letterSpacing: 1,
    color: colors.accent,
    marginLeft: space.sm,
  },
  timeOffLink: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    marginBottom: space.lg,
  },
  timeOffLinkText: {
    flex: 1,
    fontFamily: font.bodySemi,
    fontSize: 15,
    color: colors.ink,
    marginLeft: space.sm,
  },

  // Sections
  sectionTitle: {
    fontFamily: font.bodyBold,
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: colors.inkSoft,
    marginBottom: space.md,
    marginTop: space.xs,
  },


  // Day rows
  dayRow: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    marginBottom: space.sm,
    overflow: 'hidden',
  },
  dayRail: {
    width: 64,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.md,
  },
  dayRailToday: {
    backgroundColor: colors.accent,
  },
  dayName: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.5,
    color: colors.inkFaint,
  },
  dayNum: {
    fontFamily: font.display,
    fontSize: 26,
    lineHeight: 28,
    color: colors.ink,
  },
  dayTextToday: {
    color: colors.onAccent,
  },
  dayBody: {
    flex: 1,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    justifyContent: 'center',
  },
  eventRow: {
    paddingVertical: 3,
  },
  eventTime: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
  },
  eventName: {
    fontFamily: font.bodyMed,
    fontSize: 15,
    color: colors.ink,
  },
  dayOff: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkFaint,
  },
});

export default ScheduleScreen;
